import {
  applyDTODelta,
  GameMap,
  GameStateDeltaDTO,
  GameStateDTO,
  MoveResponseDTO,
  getEdgeBetween,
  HexEdge,
  IMap,
  TerrainType,
} from "@hexcrawl/shared-utils";
import {distanceInHexes} from "@hexcrawl/shared-utils";

import {MapRenderer} from '@/map/MapRenderer';
import {InputManager} from "@/map/InputManager";
import {runPredictiveAction} from '@/utils/predictiveAction'

import Phaser from 'phaser';
import Vector2 = Phaser.Math.Vector2;
import {gameService} from "@/ui/services/gameService.ts";
import {HexTargetItem, LoadingKeys, useUiStore} from "@/ui/stores/uiStore.ts";
import {useGameStore} from "@/ui/stores/gameStore.ts";
import {useEncounterStore} from "@/ui/stores/encounterStore.ts";
import {encounterService} from "@/ui/services/encounterService.ts";
import {launchCombatScene} from "@/ui/services/combatService.ts";
import { loadCharacterSprite } from '@/sprites/phaserSpriteLoader.ts';
import {AnimatedSprite, FacingDirection} from "@/utils/AnimatedSprite.ts";
import {useSpriteStore} from "@/ui/stores/spriteStore.ts";
import { useDiscoveryStore } from '@/ui/stores/discoveryStore.ts';
import { useTooltipStore } from '@/ui/stores/tooltipStore.ts';
import { useQuestStore } from '@/ui/stores/questStore.ts';
import { useLootStore } from '@/ui/stores/lootStore.ts';
import { useInventoryStore } from '@/ui/stores/inventoryStore.ts';
import { questService } from '@/ui/services/questService.ts';

export class MapScene extends Phaser.Scene {
  private gameState!: GameStateDTO;
  private map!: GameMap;

  private mapRenderer!: MapRenderer;
  private playerSprite!: AnimatedSprite;
  private inputManager!: InputManager;

  private _mouseHexPos: Vector2 = new Vector2();
  private _isPlayerMoving: boolean = false;
  private _isReloadingSprite: boolean = false;
  private _storeUnsubscribe?: () => void;
  private _spriteUnsubscribe?: () => void;
  private _discoveryUnsubscribe?: () => void;
  private _questUnsubscribe?: () => void;
  private _loreOpenUnsubscribe?: () => void;
  private _hexTargetUnsubscribe?: () => void;
  // The consumable waiting on a hex pick, mirrored from uiStore so the pointer
  // handlers can read it without a store lookup per event.
  private _hexTargetItem: HexTargetItem | null = null;

  constructor() {
    super({ key: 'MapScene' });
  }

  init(data: GameStateDTO) {
    this.setGameState(data);
    // Clear stale references from any previous run of this scene.
    // Phaser reuses scene instances, so these persist across start/stop cycles.
    this.playerSprite = undefined!;
    this._isReloadingSprite = false;

    // Subscribe to Zustand store so external state updates (e.g. camping in MapHUD)
    // are reflected in our local gameState.
    this._storeUnsubscribe?.();
    this._storeUnsubscribe = useGameStore.subscribe((state) => {
      if (state.gameState && state.gameState !== this.gameState) {
        this.setGameState(state.gameState);
      }
    });

    // Subscribe to sprite store — reload the player texture when a new hash is ready.
    this._spriteUnsubscribe?.();
    this._spriteUnsubscribe = useSpriteStore.subscribe((state) => {
      if (state.status === 'loading' && state.pendingHash && !this._isReloadingSprite) {
        this.reloadPlayerSprite(state.pendingHash);
      }
    });

    // Subscribe to discovery store — refresh map markers when discoveries change.
    this._discoveryUnsubscribe?.();
    this._discoveryUnsubscribe = useDiscoveryStore.subscribe(() => {
      if (this.mapRenderer) this.refreshDiscoveryMarkers();
    });

    // Subscribe to quest store — refresh quest markers when quests change.
    this._questUnsubscribe?.();
    this._questUnsubscribe = useQuestStore.subscribe(() => {
      if (this.mapRenderer) this.refreshQuestMarkers();
    });

    // Subscribe to the hex-target mode a consumable raises from the inventory panel.
    // Entering or leaving it changes what the hover highlight means, so the current
    // highlight is dropped and rebuilt on the next pointer move.
    this._hexTargetUnsubscribe?.();
    this._hexTargetItem = useUiStore.getState().hexTargetItem;
    this._hexTargetUnsubscribe = useUiStore.subscribe((state) => {
      if (state.hexTargetItem === this._hexTargetItem) return;
      this._hexTargetItem = state.hexTargetItem;
      this.mapRenderer?.cleearAllHighlight();
    });

    // Cancel any in-progress drag when a lore modal is opened via a map marker click.
    // The modal swallows the pointerup event, leaving InputManager stuck in drag state.
    this._loreOpenUnsubscribe?.();
    let _prevPendingLoreId: string | null = null;
    this._loreOpenUnsubscribe = useDiscoveryStore.subscribe((state) => {
      const cur = state.pendingLoreId;
      if (cur !== null && _prevPendingLoreId === null) {
        this.inputManager?.cancelDrag();
        useTooltipStore.getState().hide();
        this.input.enabled = false;
      } else if (cur === null && _prevPendingLoreId !== null) {
        this.input.enabled = true;
      }
      _prevPendingLoreId = cur;
    });
  }

  create() {
    this.mapRenderer = new MapRenderer(this, this.gameState);
    this.inputManager = new InputManager(this.input, this.cameras.main);

    // Clean up store subscriptions when scene shuts down
    this.events.once('shutdown', () => {
      this._storeUnsubscribe?.();
      this._storeUnsubscribe = undefined;
      this._spriteUnsubscribe?.();
      this._spriteUnsubscribe = undefined;
      this._discoveryUnsubscribe?.();
      this._discoveryUnsubscribe = undefined;
      this._questUnsubscribe?.();
      this._questUnsubscribe = undefined;
      this._loreOpenUnsubscribe?.();
      this._loreOpenUnsubscribe = undefined;
      this._hexTargetUnsubscribe?.();
      this._hexTargetUnsubscribe = undefined;
      // Leaving the map (for combat, say) must not strand the mode — there would be
      // no hexes to click and no way back out of it.
      useUiStore.getState().cancelHexTargeting();
    });

    // Show a generic loading overlay while assets are prepared
    useUiStore.getState().startLoading(LoadingKeys.MAP_ASSETS, 'Loading map...');

    // Begin loading assets
    this.loadData();
  }

  private setGameState(gameState: GameStateDTO | null | undefined) {
    if (!gameState || !gameState.map || !gameState.character) {
      throw new Error('Invalid game state');
    }

    this.gameState = gameState;
    this.map = new GameMap(gameState.map, useGameStore.getState().gameConfig?.movementCosts);
  }

  private updateGameState(delta: GameStateDeltaDTO) {
    console.log(`Delta: ${JSON.stringify(delta)}`);
    this.gameState = applyDTODelta(this.gameState, delta);
    this.map = new GameMap(this.gameState.map as IMap, useGameStore.getState().gameConfig?.movementCosts);
    useGameStore.getState().setGameState(this.gameState);
  }

  private async loadData() {
    await this.mapRenderer.loadTilesetData();

    // Load the player character sprite locally before starting the Phaser loader.
    // This injects the texture directly into the scene so loadSprites() skips it.
    const spriteHash = this.gameState.character?.spriteHash ?? null;
    const characterId = this.gameState.character?.id;
    if (spriteHash && characterId && !this.textures.exists(`composite-${characterId}`)) {
      try {
        await loadCharacterSprite(this, `composite-${characterId}`, spriteHash, characterId);
      } catch (err) {
        console.error('[MapScene] Failed to load player sprite locally:', err);
      }
    }

    this.loadSprites();
    this.load.once('complete', this.onLoadComplete, this);
    this.load.start();
  }

  private loadSprites() {
    // The player character sprite is assembled locally and injected into Phaser
    // in loadData() before this runs. Only non-character sprites (future use)
    // would be queued here via the standard Phaser loader.

    // Equipment icon atlases (used by inventory panel) — names come from server config
    const atlasNames = useGameStore.getState().gameConfig?.equipmentAtlases ?? [];
    for (const atlas of atlasNames) {
      if (!this.textures.exists(atlas)) {
        this.load.atlas(atlas, `/assets/images/${atlas}.png`, `/assets/images/${atlas}.json`);
      }
    }
    // Icons atlas (used for NPC markers and UI icons)
    if (!this.textures.exists('icons')) {
      this.load.atlas('icons', '/assets/images/icons.png', '/assets/images/icons.json');
    }
  }

  async onLoadComplete() {
    // Initialize sprite store with the hash that was loaded for this game session.
    useSpriteStore.getState().initialize(this.gameState.character?.spriteHash ?? null);

    // The map is on screen: release our own assets key and the adventure transition that
    // led here (raised by the lobby / map creation, and terminated by this scene).
    useUiStore.getState().stopLoading(LoadingKeys.MAP_ASSETS);
    useUiStore.getState().stopLoading(LoadingKeys.ADVENTURE_TRANSITION);

    // Blank out the background
    this.cameras.main.setBackgroundColor('#1b1b1b');

    // Allow the map renderer to complete setup
    this.mapRenderer.onDataLoaded();

    // Set up the camera
    this.setupCamera();

    this.createPlayerSprite();
    this.mapRenderer.centerCameraOnHex(this.cameras.main, this.gameState.location.x, this.gameState.location.y);
    this.setupInputControls();

    if (this.gameState.activeCombat) {
      // Defensive - we shouldn't get here.
      await launchCombatScene(this.gameState);
      return;
    }

    // Resume an in-progress encounter if one is active at the player's location.
    // Combat takes priority; if activeCombat was set, encounter resumes after combat resolves.
    if (this.gameState.activeEncounter) {
      try {
        const enc = this.gameState.activeEncounter;
        const response = await encounterService.updateNode(enc.id, null);
        useEncounterStore.getState().setActiveEncounter(response.encounter, response.node, enc.npcName);
      } catch (err) {
        console.error('Failed to resume encounter:', err);
      }
    }

    // Render discovery markers
    this.loadDiscoveryMarkers();

    // Load active quests
    this.loadQuests();
  }

  private async loadDiscoveryMarkers() {
    try {
      await useDiscoveryStore.getState().fetchDiscoveries(this.gameState.id);
      // Store subscription triggers refreshDiscoveryMarkers automatically
    } catch (err) {
      console.warn('Failed to load discovery markers:', err);
    }
  }

  private async loadQuests() {
    try {
      const quests = await questService.getQuests(this.gameState.id);
      const prevQuests = useQuestStore.getState().quests;
      useQuestStore.getState().setQuests(quests);
      // Trigger badge pulse if any quest transitioned to ready_to_complete while away
      // (e.g. after combat resolves the server-side update_quest effect)
      const becameReady = quests.some(q =>
        q.status === 'ready_to_complete' &&
        prevQuests.find(p => p.id === q.id)?.status === 'active'
      );
      if (becameReady) {
        useQuestStore.getState().triggerBadgePulse();
      }
    } catch (err) {
      console.warn('Failed to load quests:', err);
    }
  }

  private refreshDiscoveryMarkers() {
    const { npcs, items, places } = useDiscoveryStore.getState();
    const markers: Array<{id: string; x: number; y: number; name: string}> = [];

    for (const npc of npcs) {
      if (npc.x !== undefined && npc.y !== undefined) {
        markers.push({ id: npc.id, x: npc.x, y: npc.y, name: npc.name });
      }
    }
    // Only show a positional marker once the player actually knows where the
    // entity is ('located'). A merely 'known' place may carry a hex location
    // internally, but the player hasn't discovered it yet.
    for (const item of items) {
      if (item.discoveryState === 'located' && item.location.type === 'location' && item.location.id?.startsWith('hex:')) {
        const [x, y] = item.location.id.slice('hex:'.length).split(',').map(Number);
        if (Number.isFinite(x) && Number.isFinite(y)) {
          markers.push({ id: item.id, x, y, name: item.def.name });
        }
      }
    }
    for (const place of places) {
      if (place.discoveryState === 'located' && place.location.type === 'hex') {
        markers.push({ id: place.id, x: place.location.x, y: place.location.y, name: place.name });
      }
    }

    this.mapRenderer.renderDiscoveryMarkers(markers);

    // Disabled for now, not sure it looks quite what I was hoping for, but is nice for debugging.
    // const regions = places
    //   .flatMap(p => p.location.type === 'region' ? [p.location.hexes] : []);
    // this.mapRenderer.renderRegionBorders(regions);
  }

  private refreshQuestMarkers() {
    const { quests } = useQuestStore.getState();
    const markers: Array<{x: number; y: number; title: string; frame: string;}> = [];

    for (const quest of quests) {
      // An allow-list on purpose. This used to skip 'completed' and render everything else,
      // which meant any new terminal status silently kept a live marker on the map pointing
      // at an objective that is no longer there.
      const isOpen = quest.status === 'active' || quest.status === 'ready_to_complete';
      if (!isOpen) continue;
      if (quest.status === 'ready_to_complete') {
        if (quest.questGiverHex) {
          markers.push({
            x: quest.questGiverHex.x,
            y: quest.questGiverHex.y,
            title: `Return to ${quest.questGiverNpcName}`,
            frame: MapRenderer.QUEST_RETURN_FRAME
          });
        }
      } else if (quest.targetHex) {
        markers.push({
          x: quest.targetHex.x,
          y: quest.targetHex.y,
          title: quest.title,
          frame: MapRenderer.QUEST_LOCATION_FRAME
        });
      }
    }

    this.mapRenderer.renderQuestMarkers(markers);
  }

  private setupCamera() {
    const camera = this.cameras.main;

    // Set the camera bounds
    camera.setBounds(0, 0, this.mapRenderer.worldWidth, this.mapRenderer.worldHeight);

    // Begin zoomed all the way in
    camera.setZoom(this.mapRenderer.maxZoom);
  }

  private setupInputControls() {
    // Escape to return to Hello scene
    this.input.keyboard?.on('keydown-ESC', () => {
      // Backing out of hex-target mode is the one thing Escape does today, and it must
      // leave the consumable unspent.
      if (this._hexTargetItem) {
        useUiStore.getState().cancelHexTargeting();
        return;
      }
      // TODO: Leaving this in here as ESC should probably bring up a settings UI or similar,
      // but returning to the splash is clearly _not_ the right choice.
      // this.scene.start('SplashScene');
    });

    this.inputManager.on('zoom', (args) => {
      this.mapRenderer.setZoom(this.cameras.main, this.cameras.main.zoom * args.delta);
    })

    this.inputManager.on('click', (args) => {
      if (this._isPlayerMoving) return;
      if (this.gameState.status === 'ended') return;

      const clickedHexPos = this.mapRenderer.worldToHex(args.worldX, args.worldY);
      if (!clickedHexPos) return;

      // While a consumable is aimed, a click picks its destination instead of walking.
      // Even an unhighlighted hex is sent: the server owns the rules, and its refusal
      // ("You can't swim!") is the message the player needs to see.
      const targetItem = this._hexTargetItem;
      if (targetItem) {
        void this.useItemOnHex(targetItem, clickedHexPos);
        return;
      }

      if (this.isValidMove(clickedHexPos)) {
        this.movePlayer(clickedHexPos);
      }
    });

    this.inputManager.on('mouseover', (args) => {
      if (this._isPlayerMoving) return;
      const newMouseHexPos = this.mapRenderer.worldToHex(args.worldX, args.worldY);
      if (newMouseHexPos && newMouseHexPos != this._mouseHexPos) {
        this.mapRenderer?.cleearAllHighlight();
        this._mouseHexPos = newMouseHexPos;
        const selectable = this._hexTargetItem
          ? this.isValidHexTarget(this._mouseHexPos)
          : this.isValidMove(this._mouseHexPos);
        if (selectable) {
          this.mapRenderer?.setHighlight(newMouseHexPos.x, newMouseHexPos.y, true);
        }
      }
    });
  }

  /**
   * Whether a hex looks like a legal destination for a hex-targeted consumable, used
   * only to decide the hover highlight. Distance and move points are irrelevant — a
   * teleport ignores both — so this mirrors the server's rules: land, on the map. The
   * server still has the final say on any hex actually clicked.
   */
  private isValidHexTarget(hexPos: Vector2): boolean {
    const hex = this.map.getHex(hexPos.x, hexPos.y);
    return !!hex && hex.terrain !== TerrainType.WATER;
  }

  /**
   * Spends a hex-targeted consumable on the picked hex. The server treats the arrival
   * as a full hex entry, so the response is processed exactly like a walked move —
   * encounters, combat, discoveries and loot all fire.
   */
  private async useItemOnHex(item: HexTargetItem, hexPos: Vector2) {
    const gameId = this.gameState.id;

    this.mapRenderer.cleearAllHighlight();
    this.inputManager.dragEnabled = false;
    this._isPlayerMoving = true;
    useUiStore.getState().startLoading(LoadingKeys.TRAVEL, 'Folding the map...');

    try {
      const resp = await useInventoryStore.getState().useItem(gameId, item.instanceId, {
        x: hexPos.x,
        y: hexPos.y,
      });
      // Only a use the server accepted leaves aim mode; a refusal keeps the map armed
      // so the player can simply pick a different hex.
      useUiStore.getState().cancelHexTargeting();

      // Snap the sprite and camera to the destination — there is no journey to animate.
      const worldPos = this.mapRenderer.hexToWorld(hexPos.x, hexPos.y);
      if (worldPos) {
        this.playerSprite.x = worldPos.x;
        this.playerSprite.y = worldPos.y;
      }
      this.playerSprite.playAnimation('idle');
      this.centerOnHex(hexPos.x, hexPos.y);

      useUiStore.getState().stopLoading(LoadingKeys.TRAVEL);
      const transitionedToCombat = await this.processMoveResult(resp);
      if (transitionedToCombat) return;
    } catch (error: any) {
      // The item survives a rejected use, and the map stays in aim mode around it.
      useUiStore.getState().setError(error.message || `Failed to use ${item.itemName}`);
    } finally {
      useUiStore.getState().stopLoading(LoadingKeys.TRAVEL);
      this.inputManager.dragEnabled = true;
      this._isPlayerMoving = false;
    }
  }

  private isValidMove(hexPos: Vector2): boolean {
    const hex = this.map.getHex(hexPos.x, hexPos.y);
    if (!hex) return false;

    if (this.distanceToPlayer(hexPos) != 1) {
      return false;
    }

    try {
      const travelCost = this.map.getMovementCost(
        this.gameState.location.x, this.gameState.location.y,
        hexPos.x, hexPos.y,
        this.gameState.location.enterEdge
      );
      if (travelCost > this.gameState.movePoints) {
        return false;
      }
    } catch {
      return false;
    }

    return true;
  }

  private async movePlayer(hexPos: Vector2) {
    const worldPos = this.mapRenderer.hexToWorld(hexPos.x, hexPos.y);
    if (!worldPos) return;

    const newHex = this.map.getHex(hexPos.x, hexPos.y);
    if (!newHex) return;

    if (newHex.rivers && !newHex.roads) {
      const start = this.map.getHex(this.gameState.location.x, this.gameState.location.y);
      const end = this.map.getHex(hexPos.x, hexPos.y);
      if (start && end) {
        const edge = this.map.getRiverAdjustedEnterEdge(start, end, this.gameState.location.enterEdge);
        const adjacent = this.map.getNeighbor(end.x, end.y, edge);
        if (adjacent) {
          const target = this.mapRenderer.hexToWorld(adjacent.x, adjacent.y);
          if (target) {
            worldPos.x -= (worldPos.x - target.x) * 0.3;
            worldPos.y -= (worldPos.y - target.y) * 0.3;
          }
        }
      }
    }

    const gameId = this.gameState.id;

    // Predictively compute terrain cost and decrement move points immediately
    let predictedCost = this.map.getMovementCost(
      this.gameState.location.x, this.gameState.location.y,
      newHex.x, newHex.y,
      this.gameState.location.enterEdge
    );
    const previousMovePoints = this.gameState.movePoints;
    this.gameState = { ...this.gameState, movePoints: Math.max(0, previousMovePoints - predictedCost) };
    useGameStore.getState().setGameState(this.gameState);

    this.mapRenderer.cleearAllHighlight();
    const offsetX = this.playerSprite.x - this.cameras.main.worldView.centerX;
    const offsetY = this.playerSprite.y - this.cameras.main.worldView.centerY;
    this.cameras.main.startFollow(this.playerSprite, true, 0.5, 0.5, offsetX, offsetY);
    this.inputManager.dragEnabled = false;
    this._isPlayerMoving = true;

    const startSpritePos = { x: this.playerSprite.x, y: this.playerSprite.y };
    let moveTween: Phaser.Tweens.Tween | null = null;

    const moveResult = await runPredictiveAction<MoveResponseDTO>({
      startAnimation: () => new Promise<void>((resolve) => {
        const edge  = getEdgeBetween(this.gameState.location.x, this.gameState.location.y, newHex.x, newHex.y);
        this.playerSprite.setFacing(this.getFacingTowardsHexEdge(edge));
        this.playerSprite.playAnimation('walk');

        moveTween = this.tweens.add({
          targets: this.playerSprite,
          x: worldPos.x,
          y: worldPos.y,
          duration: 2000,
          ease: 'Power1',
        });
        moveTween.once(Phaser.Tweens.Events.TWEEN_COMPLETE, resolve);
        moveTween.once(Phaser.Tweens.Events.TWEEN_STOP, resolve);
      }),

      // The walk tween is done but the server hasn't answered yet — settle the sprite and
      // show the loading overlay instead of looping the walk cycle in place.
      onAwaitingResponse: () => {
        this.playerSprite.playAnimation('idle');
        useUiStore.getState().startLoading(LoadingKeys.TRAVEL, 'Rolling Dice...');
      },

      rollback: () => {
        moveTween?.stop();
        this.playerSprite.x = startSpritePos.x;
        this.playerSprite.y = startSpritePos.y;
        this.playerSprite.playAnimation('idle');
      },

      networkCall: () => {
        return gameService.movePlayer({
          gameId,
          x: hexPos.x,
          y: hexPos.y,
        });
      },

      onValidate: (moveResponse) => {
        const serverLoc = moveResponse.stateDelta.location;
        if (!serverLoc) return false;
        return serverLoc.x === hexPos.x && serverLoc.y === hexPos.y;
      },

      onSuccess: (resp: MoveResponseDTO) => {
        this.updateGameState(resp.stateDelta);
      },

      // TODO: Lift error display up from CharacterCreation to App so we can use it here, similar to the
      // loading overlay.
      onError: (error: any) => {
        // Restore move points on failure
        this.gameState = { ...this.gameState, movePoints: previousMovePoints };
        useGameStore.getState().setGameState(this.gameState);
        useUiStore.getState().setError(error.message || 'Failed to move player');
      }
    });

    useUiStore.getState().stopLoading(LoadingKeys.TRAVEL);

    const transitionedToCombat = await this.processMoveResult(moveResult);
    if (transitionedToCombat) return;

    this.cameras.main.stopFollow();
    this.playerSprite.playAnimation('idle');
    this.inputManager.dragEnabled = true;
    this._isPlayerMoving = false;
  }

  /**
   * Handles the side effects of entering a hex (discoveries, quest updates, encounter
   * overlay, combat transition) shared by normal moves and cheat teleports.
   * Returns true if it transitioned to the CombatScene (caller should stop further work).
   */
  private async processMoveResult(moveResult: MoveResponseDTO | undefined): Promise<boolean> {
    // Refresh discoveries after every move (hex entry may discover places, NPC encounter may discover NPCs)
    // Store subscription in init() will auto-refresh map markers when the store updates.
    void useDiscoveryStore.getState().fetchDiscoveries(this.gameState.id);

    // Quest accepted or completed — update quest store
    if (moveResult?.quest) {
      useQuestStore.getState().addOrUpdateQuest(moveResult.quest);
      useQuestStore.getState().triggerBadgePulse();
      if (moveResult.quest.status === 'completed') {
        useQuestStore.getState().showNotification(`Quest completed: ${moveResult.quest.title}`);
        setTimeout(() => useQuestStore.getState().clearNotification(), 4000);
      }
    }

    // If an encounter was triggered, fetch its def and activate the overlay.
    // If there's also pendingLoot (e.g. quest turn-in), hold it in the encounter
    // store so it's delivered after the encounter is dismissed — not immediately on top of it.
    if (moveResult?.encounter) {
      try {
        const enc = moveResult.encounter;
        const response = await encounterService.updateNode(enc.id, null);
        useEncounterStore.getState().setActiveEncounter(response.encounter, response.node, enc.npcName, moveResult.appliedEffects, moveResult.pendingLoot);
      } catch (err) {
        console.error('Failed to load encounter node:', err);
      }
    } else if (moveResult?.pendingLoot) {
      // No encounter — apply loot immediately
      useLootStore.getState().setPendingLoot(moveResult.pendingLoot);
    }

    // If combat was started as a side effect of the encounter, transition to CombatScene
    if (moveResult?.combat) {
      const updatedGameState = { ...this.gameState, activeCombat: moveResult.combat };
      this.gameState = updatedGameState;
      useGameStore.getState().setGameState(updatedGameState);
      try { window.phaserGame?.scene?.stop('MapScene'); } catch {}
      await launchCombatScene(updatedGameState);
      return true;
    }

    return false;
  }

  /**
   * Cheat: instantly teleport the player to a hex and process it as a normal hex entry
   * (triggering encounters, discoveries, etc.). Skips the walk animation and
   * movement-point cost.
   */
  async jumpToHex(x: number, y: number) {
    if (this._isPlayerMoving) return;
    const gameId = this.gameState.id;

    this._isPlayerMoving = true;
    this.inputManager.dragEnabled = false;
    this.mapRenderer.cleearAllHighlight();

    try {
      const resp = await gameService.movePlayer({ gameId, x, y, cheatTeleport: true });
      this.updateGameState(resp.stateDelta);

      // Snap the player sprite and camera to the destination.
      const worldPos = this.mapRenderer.hexToWorld(x, y);
      if (worldPos) {
        this.playerSprite.x = worldPos.x;
        this.playerSprite.y = worldPos.y;
      }
      this.playerSprite.playAnimation('idle');
      this.centerOnHex(x, y);

      const transitionedToCombat = await this.processMoveResult(resp);
      if (transitionedToCombat) return;
    } catch (error: any) {
      useUiStore.getState().setError(error.message || 'Failed to teleport player');
    } finally {
      this.inputManager.dragEnabled = true;
      this._isPlayerMoving = false;
    }
  }

  private getFacingTowardsHexEdge(edge: HexEdge): FacingDirection {
    switch (edge) {
      case HexEdge.NE:
        return 'northeast';
      case HexEdge.NW:
        return 'northwest';
      case HexEdge.SE:
        return 'southeast';
      case HexEdge.SW:
        return 'southwest';
      case HexEdge.E:
        return 'east';
      case HexEdge.W:
        return 'west';
    }
    throw new Error(`Invalid edge: ${edge}`);
  }

  private createPlayerSprite() {
    if (this.playerSprite) return;

    const loc = this.gameState.location;
    const characterId = this.gameState.character?.id ?? '';
    const playerTextureKey = `composite-${characterId}`;
    const sprite = this.mapRenderer.createSprite(playerTextureKey, loc.x, loc.y);
    if (!sprite) throw new Error('Failed to create player sprite');

    sprite.setOrigin(0.5, 1);

    if (loc.enterEdge) {
      const hex = this.map.getHex(loc.x, loc.y);
      if (hex && hex.rivers && !hex.roads) {
        const adjacent = this.map.getNeighbor(loc.x, loc.y, loc.enterEdge);
        if (adjacent) {
          const target = this.mapRenderer.hexToWorld(adjacent.x, adjacent.y);
          if (target) {
            sprite.x -= (sprite.x - target.x) * 0.3;
            sprite.y -= (sprite.y - target.y) * 0.3;
          }
        }
      }
    }

    this.playerSprite = new AnimatedSprite(sprite, playerTextureKey);
  }

  private async reloadPlayerSprite(newHash: string) {
    const characterId = this.gameState.character?.id;
    if (!characterId) return;

    this._isReloadingSprite = true;

    // Update local copy so downstream code uses the new hash.
    this.gameState = {
      ...this.gameState,
      character: { ...this.gameState.character!, spriteHash: newHash },
    };

    // Destroy old AnimatedSprite first — removes it from the scene display list
    // so no frame-update ticks can fire against it after we tear down the texture.
    const pos = this.playerSprite ? { x: this.playerSprite.x, y: this.playerSprite.y } : null;
    if (this.playerSprite) {
      this.playerSprite.destroy();
      this.playerSprite = undefined!;
    }

    try {
      // loadCharacterSprite handles texture + animation teardown internally.
      await loadCharacterSprite(this, `composite-${characterId}`, newHash, characterId);
      this.createPlayerSprite();
      if (pos) {
        this.playerSprite.x = pos.x;
        this.playerSprite.y = pos.y;
      }
      this.playerSprite?.playAnimation('idle');
      useSpriteStore.getState().notifySpriteLoaded(newHash);
    } catch {
      useSpriteStore.getState().notifySpriteLoadError('Failed to load sprite texture');
    } finally {
      this._isReloadingSprite = false;
    }
  }

  centerOnHex(x: number, y: number) {
    this.mapRenderer.centerCameraOnHex(this.cameras.main, x, y);
  }

  private distanceToPlayer(hexPos: Vector2) {
    return distanceInHexes(this.gameState.location.x, this.gameState.location.y, hexPos.x, hexPos.y);
  }
}
